import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ShiftGlobalSearchCommandPalette } from "./ShiftGlobalSearchCommandPalette";
import {
	getSurfaceBorderColor,
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import {
	GetLanguageValueAsString,
	Translate,
} from "@/app/application/Translate";
import { UnifiedPageHeader } from "@/app/application/UnifiedPageHeader";
import { TranslationContext, UserContext } from "@/app/providers";
import { checkAdminAccess } from "@/service/ApplicationQueries";
import type { ShiftSearchFilterOptions } from "@/features/shift/hooks";
import { Language } from "@/model/Language/Language";
import {
	filterPanelDatetimeWidth,
	sharedFilterPanelSx,
	sharedFilterResetButtonSx,
	sharedSecondaryButtonSx,
	unifiedHeaderTokens,
} from "@/styles/sharedNavStyles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	Menu,
	MenuItem,
	Select,
	TextField,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import dayjs from "dayjs";

/* ────────────────────────────────────────────────────────── */
/* Context: tab-specific header actions (e.g. Save and clear)   */
/* ────────────────────────────────────────────────────────── */

export type SetHeaderActions = (node: React.ReactNode) => void;

const ShiftHeaderActionsContext = createContext<SetHeaderActions | null>(null);

export function useShiftHeaderActions() {
	const set = useContext(ShiftHeaderActionsContext);
	return set ?? (() => {});
}

export const ShiftHeaderActionsContextProvider =
	ShiftHeaderActionsContext.Provider;

/* ────────────────────────────────────────────────────────── */
/* Header component — uses UnifiedPageHeader (4-row structure)  */
/* ────────────────────────────────────────────────────────── */

export interface ShiftPageHeaderProps {
	onRegisterClick: () => void;
	onOverviewClick: () => void;
	selectedGroup: string;
	onGroupChange: (value: string) => void;
	searchInput: string;
	onSearchChange: (value: string) => void;
	extraActions?: React.ReactNode;
	shiftOptions?: { Groups?: Array<{ Id: number; Name?: string }> } | null;
	loading?: boolean;
	/** Current tab index (4 = Search). When Search tab, shows Start/End dates + checkboxes first. */
	activeTabIndex?: number;
	startFromUtc?: string;
	setStartFromUtc?: (v: string) => void;
	endAtUtc?: string;
	setEndAtUtc?: (v: string) => void;
	searchFilterOptions?: ShiftSearchFilterOptions;
	setSearchFilterOptions?: React.Dispatch<
		React.SetStateAction<ShiftSearchFilterOptions>
	>;
	/** Row 4: Tabs (fixed slot per unified header spec) */
	tabs?: React.ReactNode;
}

const SEARCH_TAB_INDEX = 4;

export const ShiftPageHeader: React.FC<ShiftPageHeaderProps> = ({
	onRegisterClick,
	onOverviewClick,
	selectedGroup,
	onGroupChange,
	searchInput,
	onSearchChange,
	extraActions,
	shiftOptions,
	loading,
	activeTabIndex = 0,
	startFromUtc = "",
	setStartFromUtc,
	endAtUtc = "",
	setEndAtUtc,
	searchFilterOptions = {
		all: true,
		outOfService: false,
		handoverEvent: false,
	},
	setSearchFilterOptions,
	tabs,
}) => {
	const user = useContext(UserContext);
	const isAdmin = Boolean(user && checkAdminAccess(user));
	const translation: Language | null = useContext(TranslationContext);
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");
	const [showFilters, setShowFilters] = useState(false);
	const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null);
	const [globalSearchOpen, setGlobalSearchOpen] = useState(false);

	const buildShiftSearchEventUrl = (
		row: { Id?: number | string } | null | undefined,
	) => {
		const eventId = Number(row?.Id);
		if (!Number.isFinite(eventId)) return null;

		return `/shift?tab=search&eventId=${eventId}`;
	};

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				setGlobalSearchOpen(true);
			}
			if (e.key === "Escape") setGlobalSearchOpen(false);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);
	if (loading || !shiftOptions?.Groups?.length) {
		return null;
	}

	const CONTROL_H = isMobile ? 44 : unifiedHeaderTokens.buttonHeight;

	const controlSx = {
		height: CONTROL_H,
		minHeight: CONTROL_H,
		borderRadius: 2,
	};

	const selectSx = {
		"& .MuiInputBase-root": {
			...controlSx,
			bgcolor: "background.paper",
			border: getSurfaceBorderStyle(theme, "strong"),
		},
		"& .MuiSelect-select": {
			display: "flex",
			alignItems: "center",
			height: CONTROL_H,
			py: 0, // critical: avoid MUI default vertical padding drifting
		},
	};

	const textFieldSx = (t: Theme) => ({
		"& .MuiInputBase-root": {
			...controlSx,
			bgcolor: "background.paper",
			border: getSurfaceBorderStyle(t, "strong"),
			boxShadow: "none",
			"&.Mui-focused": {
				boxShadow: `0 0 0 2px ${alpha(t.palette.primary.main, 0.12)}`,
			},
		},
		"& .MuiInputBase-input": {
			height: CONTROL_H,
			py: 0, // critical
			display: "flex",
			alignItems: "center",
			fontSize: unifiedHeaderTokens.searchInputFontSize,
		},
	});

	const isSearchTab = activeTabIndex === SEARCH_TAB_INDEX;
	const showSearchFilters =
		isSearchTab && setStartFromUtc && setEndAtUtc && setSearchFilterOptions;

	const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = event.target;
		setSearchFilterOptions?.((prev) => {
			let next = { ...prev };

			if (name === "all") {
				if (checked) {
					// Selecting "all" clears specific filters
					next = {
						...next,
						all: true,
						outOfService: false,
						handoverEvent: false,
					};
				} else {
					// Prevent ending up with no filters at all selected:
					// if no specific filters are active, ignore unchecking "all"
					if (!prev.outOfService && !prev.handoverEvent) {
						return prev;
					}
					next = {
						...next,
						all: false,
					};
				}
			} else if (name === "outOfService" || name === "handoverEvent") {
				next = {
					...next,
					[name]: checked,
				};
				// Derive "all" from the specific filters: it's true only when none are selected
				next.all = !(next.outOfService || next.handoverEvent);
			} else {
				next = {
					...next,
					[name]: checked,
				};
			}

			return next;
		});
	};

	const hasActiveSearchFilters =
		showSearchFilters &&
		(startFromUtc !== "" ||
			!searchFilterOptions.all ||
			searchFilterOptions.outOfService ||
			searchFilterOptions.handoverEvent);

	const handleClearSearchFilters = () => {
		setStartFromUtc?.("");
		setEndAtUtc?.(dayjs().format("YYYY-MM-DDTHH:mm"));
		setSearchFilterOptions?.({
			all: true,
			outOfService: false,
			handoverEvent: false,
		});
	};

	const filterBar =
		showSearchFilters && showFilters ? (
			<Box
				sx={[
					sharedFilterPanelSx(theme),
					{
						mt: 0.5,
						p: 1,
						borderRadius: 2,
					},
				]}
			>
				{/* Top row: title + clear */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
					}}
				>
					{hasActiveSearchFilters && (
						<Button
							variant="text"
							size="small"
							startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
							onClick={handleClearSearchFilters}
							sx={sharedFilterResetButtonSx}
						>
							<Translate String="Procedures.Filter.Reset">Clear all</Translate>
						</Button>
					)}
				</Box>

				{/* Date range + toggles */}
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						alignItems: { xs: "stretch", md: "center" },
						gap: 1,
					}}
				>
					{/* Date range group */}
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "column", sm: "row" },
							alignItems: { xs: "stretch", sm: "center" },
							gap: 0.75,
							minWidth: 0,
							flex: "1 1 auto",
						}}
					>
						<Typography
							variant="body2"
							sx={{
								fontWeight: 600,
								color: "text.secondary",
								flexShrink: 0,
								lineHeight: { sm: "32px" },
							}}
						>
							<Translate String="Archive.Filter.DateRange">
								Date range
							</Translate>
						</Typography>

						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								alignItems: "center",
								gap: 0.75,
								minWidth: 0,
							}}
						>
							<TextField
								type="datetime-local"
								size="small"
								hiddenLabel
								value={
									startFromUtc
										? dayjs(startFromUtc).format("YYYY-MM-DDTHH:mm")
										: ""
								}
								onChange={(e) =>
									setStartFromUtc(dayjs(e.target.value).utc().format())
								}
								InputLabelProps={{ shrink: true }}
								sx={{
									width: { xs: "100%", sm: filterPanelDatetimeWidth },
									minWidth: { xs: 0, sm: filterPanelDatetimeWidth },
								}}
							/>

							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									px: 0.25,
									display: { xs: "none", sm: "block" },
									userSelect: "none",
								}}
							>
								→
							</Typography>

							<TextField
								type="datetime-local"
								size="small"
								hiddenLabel
								value={
									endAtUtc ? dayjs(endAtUtc).format("YYYY-MM-DDTHH:mm") : ""
								}
								onChange={(e) =>
									setEndAtUtc(dayjs(e.target.value).utc().format())
								}
								InputLabelProps={{ shrink: true }}
								sx={{
									width: { xs: "100%", sm: filterPanelDatetimeWidth },
									minWidth: { xs: 0, sm: filterPanelDatetimeWidth },
								}}
							/>
						</Box>
					</Box>

					{/* Toggle group */}
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							justifyContent: { xs: "flex-start", md: "flex-end" },
							gap: 0.5,
						}}
					>
						<FormControlLabel
							sx={{
								m: 0,
								px: 0.75,
								py: 0.25,
								borderRadius: 2,
								"&:hover": { bgcolor: "action.hover" },
								"& .MuiFormControlLabel-label": {
									color: "text.secondary",
								},
							}}
							control={
								<Checkbox
									checked={searchFilterOptions.all}
									onChange={handleFilterChange}
									name="all"
									color="primary"
									sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
								/>
							}
							label={<Translate String="Shift.Event.All">All</Translate>}
						/>

						<FormControlLabel
							sx={{
								m: 0,
								px: 0.75,
								py: 0.25,
								borderRadius: 2,
								"&:hover": { bgcolor: "action.hover" },

								"& .MuiFormControlLabel-label": {
									color: "text.secondary",
								},
							}}
							control={
								<Checkbox
									checked={searchFilterOptions.outOfService}
									onChange={handleFilterChange}
									name="outOfService"
									color="info"
									sx={{
										"& .MuiSvgIcon-root": { fontSize: 18 },
									}}
								/>
							}
							label={
								<Translate String="Shift.Event.OutOfService">
									Out of service
								</Translate>
							}
						/>

						<FormControlLabel
							sx={{
								m: 0,
								px: 0.75,
								py: 0.25,
								borderRadius: 2,
								"&:hover": { bgcolor: "action.hover" },
								"& .MuiFormControlLabel-label": {
									color: "text.secondary",
								},
							}}
							control={
								<Checkbox
									checked={searchFilterOptions.handoverEvent}
									onChange={handleFilterChange}
									name="handoverEvent"
									color="primary"
									sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
								/>
							}
							label={
								<Translate String="Shift.Event.Handover">
									<Typography variant="body2" color="text.secondary">
										Handover
									</Typography>
								</Translate>
							}
						/>
					</Box>
				</Box>
			</Box>
		) : null;

	const hasActionsMenu = Boolean(extraActions);
	const actionsOpen = hasActionsMenu && Boolean(actionsAnchor);

	const registerButtonLabel = (
		<Translate String="Shift.Event.RegisterEvent">
			+ New shift
		</Translate>
	);

	const actionsMenu = (
		<Menu
			anchorEl={actionsAnchor}
			open={actionsOpen}
			onClose={() => setActionsAnchor(null)}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			MenuListProps={{
				dense: false,
				onClick: () => setActionsAnchor(null),
				sx: { py: 0.5 },
			}}
			PaperProps={{
				sx: {
					borderRadius: 3,
					mt: 1,
					minWidth: 240,
					overflow: "hidden",
					border: (theme) => `1px solid ${theme.palette.divider}`,
					boxShadow:
						"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
				},
			}}
		>
			<Box
				sx={{
					"& .MuiMenuItem-root": {
						mx: 0.75,
						my: 0.25,
						borderRadius: 2,
						px: 1,
						py: 0.75,
						minHeight: 38,
					},
					"& .MuiListItemIcon-root": {
						minWidth: 30,
						color: "text.primary",
					},
					"& .MuiListItemText-primary": {
						fontSize: 14.5,
						fontWeight: 500,
						letterSpacing: 0.1,
					},
				}}
			>
				{extraActions}
			</Box>
		</Menu>
	);

	const secondaryActions = (
		<>
			{showSearchFilters && (
				<Button
					variant="outlined"
					size="small"
					startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
					onClick={() => setShowFilters((v) => !v)}
					color={showFilters ? "primary" : "inherit"}
					sx={{
						...sharedSecondaryButtonSx,
						textTransform: "none",
						fontWeight: 600,
						pl: 1,
						pr: 0.5,
						bgcolor: showFilters ? "action.selected" : "transparent",
					}}
				>
					<Translate String="Procedures.Filters">Filters</Translate>

					<ExpandMoreIcon
						sx={{
							fontSize: 18,
							ml: 0.25,
							transition: "transform 150ms ease",
							transform: showFilters ? "rotate(180deg)" : "rotate(0deg)",
						}}
					/>
				</Button>
			)}
			{isAdmin && (
				<Button
					variant="outlined"
					size="small"
					startIcon={<InsightsOutlinedIcon sx={{ fontSize: 18 }} />}
					onClick={onOverviewClick}
				sx={sharedSecondaryButtonSx}
			>
				<Translate String="ShiftPageHeader.Overview">Overview</Translate>
			</Button>
			)}
			<Button
				variant="contained"
				size="small"
				startIcon={<AddIcon sx={{ fontSize: 18 }} />}
				onClick={onRegisterClick}
				sx={sharedSecondaryButtonSx}
			>
				{registerButtonLabel}
			</Button>
			{hasActionsMenu && (
				<Button
					variant="contained"
					size="small"
					onClick={(e) => setActionsAnchor(e.currentTarget)}
					endIcon={<span style={{ fontSize: 10 }}>▾</span>}
					sx={sharedSecondaryButtonSx}
				>
					<Translate String="Shift.Header.Actions">Actions</Translate>
				</Button>
			)}
		</>
	);

	const mobileActionButtonSx = {
		...sharedSecondaryButtonSx,
		...controlSx,
		flex: "1 1 160px",
		minWidth: 0,
		borderRadius: 2,
		textTransform: "none",
		fontWeight: 700,
		justifyContent: "center",
	};

	const globalSearchButton = (
		<Button
			onClick={() => setGlobalSearchOpen(true)}
			variant="outlined"
			startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
			sx={[
				sharedSecondaryButtonSx,
				(t) => ({
					height: CONTROL_H,
					minHeight: CONTROL_H,
					minWidth: 280,
					justifyContent: "space-between",
					alignItems: "center",
					textTransform: "none",
					fontWeight: 900,
					borderRadius: 2,
					borderColor: getSurfaceBorderColor(t, "strong"),
					bgcolor: t.palette.background.paper,
					"&:hover": {
						borderColor: getSurfaceBorderColor(t, "strong"),
						bgcolor: alpha(
							t.palette.primary.main,
							t.palette.mode === "dark" ? 0.08 : 0.035,
						),
					},
				}),
			]}
		>
			<Box sx={{ flex: 1, textAlign: "left", opacity: 0.85 }}>
				<Translate String="Auto.Features.Shift.Ui.Components.ShiftPageHeader.L623C60.SearchEverythingInShift">
					Search everything in shift…
				</Translate>
			</Box>
			<Box
				sx={(t) => ({
					ml: 1,
					px: 0.75,
					py: 0.25,
					borderRadius: 1,
					fontSize: 12,
					fontWeight: 900,
					color: "text.secondary",
					border: getSurfaceBorderStyle(t, "strong"),
				})}
			>
				Ctrl K
			</Box>
		</Button>
	);

	const localSearchField = (
		<TextField
			id={isMobile ? "shift-search-mobile" : "shift-search"}
			size="small"
			placeholder={GetLanguageValueAsString(
				translation,
				"Auto.Features.Shift.Ui.Components.ShiftPageHeader.L647C16.SearchInThisView",
				"Search in this view…",
			)}
			value={searchInput}
			onChange={(e) => onSearchChange(e.target.value)}
			fullWidth
			InputProps={{
				startAdornment: (
					<InputAdornment position="start" sx={{ mr: 0 }}>
						<SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
					</InputAdornment>
				),
				endAdornment: searchInput ? (
					<InputAdornment position="end">
						<IconButton
							size="small"
							aria-label={GetLanguageValueAsString(
								translation,
								"Auto.Features.Shift.Ui.Components.ShiftPageHeader.L661C19.ClearLocalSearch",
								"Clear local search",
							)}
							onClick={() => onSearchChange("")}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</InputAdornment>
				) : undefined,
			}}
			sx={textFieldSx}
		/>
	);

	const groupSelect = (
		<FormControl
			size="small"
			sx={{
				width: { xs: "100%", sm: 220 },
				minWidth: 0,
				...selectSx,
			}}
		>
			<InputLabel id="shift-header-group-label">
				<Translate String="Shift.Event.Group">Group</Translate>
			</InputLabel>

			<Select
				labelId="shift-header-group-label"
				label="Group"
				value={selectedGroup}
				onChange={(e) => onGroupChange(e.target.value)}
				sx={{
					"& .MuiOutlinedInput-notchedOutline": { borderRadius: 2 },
				}}
			>
				<MenuItem value="all">
					<Translate String="Shift.Event.AllGroups">All Groups</Translate>
				</MenuItem>
				{shiftOptions?.Groups?.map((item) => {
					if (user && user.Groups.some((g) => g.Id === item.Id)) {
						return (
							<MenuItem key={item.Id} value={String(item.Id)}>
								<Typography variant="body2" component="span">
									{item.Name}
								</Typography>
							</MenuItem>
						);
					}
					return null;
				})}
			</Select>
		</FormControl>
	);

	return (
		<UnifiedPageHeader
			searchOrFilter={
				<>
					<ShiftGlobalSearchCommandPalette
						open={globalSearchOpen}
						onClose={() => setGlobalSearchOpen(false)}
						onOpenItem={(row: { Id?: number | string }) => {
							const nextUrl = buildShiftSearchEventUrl(row);
							if (nextUrl) navigate(nextUrl);
						}}
						buildItemUrl={buildShiftSearchEventUrl}
					/>
					{hasActionsMenu && actionsMenu}

					{isMobile ? (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1,
								width: "100%",
							}}
						>
							{localSearchField}

							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									alignItems: "stretch",
									gap: 0.75,
									width: "100%",
								}}
							>
								<Button
									variant="outlined"
									startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
									onClick={() => setGlobalSearchOpen(true)}
									sx={mobileActionButtonSx}
								>
									<Translate String="Auto.Features.Shift.Ui.Components.ShiftPageHeader.L754C10.SearchAll">
										Search all
									</Translate>
								</Button>

								{showSearchFilters && (
									<Button
										variant="outlined"
										startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
										onClick={() => setShowFilters((v) => !v)}
										color={showFilters ? "primary" : "inherit"}
										sx={{
											...mobileActionButtonSx,
											bgcolor: showFilters ? "action.selected" : "transparent",
										}}
									>
										Filters
									</Button>
								)}
							{isAdmin && (
								<Button
									variant="outlined"
									startIcon={<InsightsOutlinedIcon sx={{ fontSize: 18 }} />}
									onClick={onOverviewClick}
									sx={mobileActionButtonSx}
								>
									<Translate String="ShiftPageHeader.Overview">
										Overview
									</Translate>
								</Button>
							)}
								<Button
									variant="contained"
									startIcon={<AddIcon sx={{ fontSize: 18 }} />}
									onClick={onRegisterClick}
									sx={mobileActionButtonSx}
								>
									{registerButtonLabel}
								</Button>
								{hasActionsMenu && (
									<Button
										variant="contained"
										onClick={(e) => setActionsAnchor(e.currentTarget)}
										sx={{
											...mobileActionButtonSx,
											px: 1.5,
										}}
									>
										<Translate String="Shift.Header.Actions">Actions</Translate>
									</Button>
								)}
							</Box>

							{groupSelect}
						</Box>
					) : (
						<Box
							sx={{
								display: "flex",
								flexDirection: { xs: "column", md: "row" },
								gap: 1,
								alignItems: { xs: "stretch", md: "center" },
								width: "100%",
							}}
						>
							<Box sx={{ flex: "0 0 auto" }}>{globalSearchButton}</Box>
							<Box sx={{ flex: 1, minWidth: 220 }}>{localSearchField}</Box>
							{groupSelect}
						</Box>
					)}
				</>
			}
			secondaryActions={isMobile ? undefined : secondaryActions}
			row4Content={
				showSearchFilters ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							width: "100%",
							minHeight: 0,
						}}
					>
						{tabs}
						{filterBar}
					</Box>
				) : (
					tabs
				)
			}
			data-tour="shift-actions"
		/>
	);
};
